import React from "react";

import useSWR from "swr";

import { swrFetcher } from "../axiosClient.js";
import { readableDocumentNumber } from "../components/common/ReadableDocumentNumber.jsx";

const DATA_REFRESH_INTERVAL_MS = 15 * 60 * 1000;
const DocumentContext = React.createContext();

const documentIsApproved = (document, minimumVersion = "1.0") => {
  if (!document) {
    return false;
  }
  if (document.workflow_state.includes("Approved")) {
    return true;
  }
  if (document.last_approved_version) {
    const [versionMajorString, versionMinorString] = document.last_approved_version.split(".");
    const versionMajor = Number(versionMajorString);
    const versionMinor = Number(versionMinorString);
    const [targetMajorString, targetMinorString] = minimumVersion.split(".");
    const targetMajor = Number(targetMajorString);
    const targetMinor = Number(targetMinorString);

    if (versionMajor > targetMajor) {
      return true;
    }
    if (versionMajor < targetMajor) {
      return false;
    }
    if (versionMinor >= targetMinor) {
      return true;
    }
  }
  return false;
};

export function DocumentProvider({ children }) {
  //---------------------------------------------------------------------------
  // Fetch the data using SWR
  //---------------------------------------------------------------------------
  const {
    data: documentsData,
    error: swrError,
    mutate,
  } = useSWR({ url: "/api/kt" }, swrFetcher, {
    refreshInterval: DATA_REFRESH_INTERVAL_MS,
  });

  const documents = React.useMemo(() => documentsData || [], [documentsData]);

  // Memoize the value to reduce rerenders
  const value = React.useMemo(
    () => ({
      documents,
      documentIsApproved,
      documentOptions: documents.map((document) => ({
        id: document.id,
        key: document.id,
        name: readableDocumentNumber(document.id, documents),
      })),
      error: swrError ? swrError.response?.data?.error || swrError.message : null,
      mutate,
    }),
    [documents, mutate, swrError]
  );

  return <DocumentContext.Provider value={value}>{children}</DocumentContext.Provider>;
}

export default DocumentContext;
